import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Cropper from 'react-easy-crop';
import toast from 'react-hot-toast';
import heic2any from 'heic2any';
import Resizer from 'react-image-file-resizer';

import Button from '@mui/material/Button';
import Slider from '@mui/material/Slider';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { getOrientation } from 'get-orientation/browser';
import Typography from '@mui/material/Typography';

import { _lt } from '../../localization/locale';
import {
  getCroppedImg,
  getRotatedImage,
  getImageFile,
} from '../../utils/canvas';
import { readFile } from '../../utils/images';
import { useTheme } from '@mui/material/styles';

const resizeFile = (file) =>
  new Promise((resolve) => {
    Resizer.imageFileResizer(
      file,
      1000,
      1000,
      'PNG',
      95,
      0,
      (uri) => {
        resolve(uri);
      },
      'file',
    );
  });

const ORIENTATION_TO_ANGLE = {
  3: 180,
  6: 90,
  8: -90,
};

const ImageUpload = (props) => {
  const theme = useTheme();

  const classes = {
    cropContainer: {
      position: 'relative',
      width: '100%',
      height: 150,
      background: '#333',
      [theme.breakpoints.up('sm')]: {
        height: 300,
      },
    },
    cropButton: {
      flexShrink: 0,
      marginLeft: 16,
    },
    controls: {
      padding: 16,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      [theme.breakpoints.up('sm')]: {
        flexDirection: 'row',
        alignItems: 'center',
      },
    },
    sliderContainer: {
      display: 'flex',
      flex: '1',
      alignItems: 'center',
    },
    sliderLabel: {
      [theme.breakpoints.down('xs')]: {
        minWidth: 65,
      },
    },
    slider: {
      padding: '22px 0px',
      marginLeft: 32,
      [theme.breakpoints.up('sm')]: {
        flexDirection: 'row',
        alignItems: 'center',
        margin: '0 16px',
      },
    },
  };

  const { handleClose, handleUpload, show, aspectRatio, roundShape } = props;

  const [imageSrc, setImageSrc] = useState(null);
  const [btnText, setBtnText] = useState('Choose Image');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [disabled, setDisabled] = useState(false);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const resetState = () => {
    setCroppedAreaPixels(null);
    setZoom(1);
    setRotation(0);
    setCrop({ x: 0, y: 0 });
    setImageSrc(null);
  };

  const onFileChange = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      let file = e.target.files[0];
      // if not image
      if (file.type.indexOf('image') === -1) return;

      if (file.type.indexOf('heic') > 0) {
        setBtnText('Processing image...');
        heic2any({
          blob: file,
          toType: 'image/png',
          quality: 0.9,
        })
          .then(async (resultBlob) => {
            resultBlob['name'] = 'file.png';

            if (resultBlob.size > 2000000)
              resultBlob = await resizeFile(resultBlob);
            console.log('resized resultBlob', resultBlob);

            setBtnText('Choose Image');

            let imageDataUrl = await readFile(resultBlob);

            // apply rotation if needed, it might throw an error, in that case we consider it 1
            let imageOrientation = 1;
            try {
              imageOrientation = await getOrientation(resultBlob);
            } catch (e) {
              console.error('getOrientation', e);
            }

            const rotation = ORIENTATION_TO_ANGLE[imageOrientation];
            if (rotation) {
              imageDataUrl = await getRotatedImage(imageDataUrl, rotation);
            }

            setImageSrc(imageDataUrl);
          })
          .catch(function (x) {
            console.log(x.code);
            console.log(x.message);
          });
      } else {
        setBtnText('Processing image...');
        if (file.size > 2000000) file = await resizeFile(file);

        setBtnText('Choose Image');

        let imageDataUrl = await readFile(file);

        // apply rotation if needed, it might throw an error, in that case we consider it 1
        let imageOrientation = 1;
        try {
          imageOrientation = await getOrientation(file);
        } catch (e) {
          console.error('getOrientation', e);
        }
        const rotation = ORIENTATION_TO_ANGLE[imageOrientation];
        if (rotation) {
          imageDataUrl = await getRotatedImage(imageDataUrl, rotation);
        }
        setImageSrc(imageDataUrl);
      }
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const generateCroppedImage = useCallback(async () => {
    try {
      const croppedImage = await getCroppedImg(
        imageSrc,
        croppedAreaPixels,
        rotation,
      );
      const croppedFile = await getImageFile(
        imageSrc,
        croppedAreaPixels,
        rotation,
      );
      setDisabled(false);
      return handleUpload(croppedImage, croppedFile);
    } catch (e) {
      setDisabled(false);
      console.error(e);
      toast.error(
        _lt(
          'Unfotunately could not process this image, please use another smaller image',
        ),
      );
    }
  }, [imageSrc, croppedAreaPixels, rotation]);

  return (
    <Dialog
      open={show}
      onClose={() => {
        resetState();
        return handleClose();
      }}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle sx={{ paddingBottom: 0 }}>{props.header}</DialogTitle>
      <DialogContent>
        <DialogContentText pb={1}>
          <Typography variant="caption">
            {props.detail ||
              _lt('Press UPLOAD IMAGE to save the finalised photo')}
          </Typography>
        </DialogContentText>
        {imageSrc && (
          <div>
            <div style={classes.cropContainer}>
              <Cropper
                image={imageSrc}
                crop={crop}
                rotation={rotation}
                zoom={zoom}
                aspect={aspectRatio || 1}
                cropShape={roundShape && 'round'}
                showGrid={false}
                onCropChange={setCrop}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
            </div>
            <div style={classes.controls}>
              <div style={classes.sliderContainer}>
                <Typography
                  variant="overline"
                  classes={{ root: classes.sliderLabel }}
                >
                  {_lt('Zoom')}
                </Typography>
                <Slider
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.1}
                  aria-labelledby="Zoom"
                  classes={{ root: classes.slider }}
                  onChange={(e, zoom) => setZoom(zoom)}
                />
              </div>
              <div style={classes.sliderContainer}>
                <Typography
                  variant="overline"
                  classes={{ root: classes.sliderLabel }}
                >
                  {_lt('Rotation')}
                </Typography>
                <Slider
                  value={rotation}
                  min={0}
                  max={360}
                  step={1}
                  aria-labelledby="Rotation"
                  classes={{ root: classes.slider }}
                  onChange={(e, rotation) => setRotation(rotation)}
                />
              </div>
            </div>
            <Button
              onClick={(e) => {
                setDisabled(true);
                return generateCroppedImage();
              }}
              color="secondary"
              variant="contained"
              disabled={disabled}
            >
              {_lt('Upload Image')}
            </Button>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          component="label"
          variant="contained"
          onChange={onFileChange}
          accept="image/*"
          color="primary"
        >
          {_lt(btnText)}
          <input type="file" accept="image/*" hidden />
        </Button>
        <Button
          onClick={() => {
            resetState();
            return handleClose();
          }}
          color="primary"
        >
          {_lt('Cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ImageUpload.propTypes = {
  handleClose: PropTypes.func.isRequired,
  handleUpload: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
  aspectRatio: PropTypes.number,
  roundShape: PropTypes.bool,
};

const mapStateToProps = () => ({});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(ImageUpload);
